import { getActiveArtistIds } from './api'

/** Rolling window: show digest again after this many ms (persists in localStorage across tabs/refreshes). */
export const DIGEST_INTERVAL_MS = 24 * 60 * 60 * 1000

const STORAGE_V2_PREFIX = 'dizee:digest:lastSeenAt:v2:'
/** Legacy keys used `dizee:digest:lastSeen:${id}` where `id` was sometimes account `_id` and sometimes `getEffectiveUserId()`. */
const LEGACY_PREFIX = 'dizee:digest:lastSeen:'

/**
 * Logged-in account id (`dizeeUser._id`). Use this for eligibility + “seen” timestamps so team members
 * get one cadence per login, not per selected artist.
 */
export function getDigestAccountUserId(): string | null {
  try {
    const raw = localStorage.getItem('dizeeUser')
    const user = raw ? JSON.parse(raw) : null
    return user?._id ? String(user._id) : null
  } catch {
    return null
  }
}

/** Team managers/support/promoters must pick at least one active artist before the main app (same rule as `/` redirect). */
export function teamMemberMustSelectArtistFirst(): boolean {
  try {
    const raw = localStorage.getItem('dizeeUser')
    const user = raw ? JSON.parse(raw) : null
    const isTeamMember =
      typeof user?.professionRole === 'string' &&
      ['manager', 'support', 'promoter'].includes(user.professionRole)
    if (!isTeamMember) return false
    return getActiveArtistIds().length === 0
  } catch {
    return false
  }
}

function readLastSeenMs(accountId: string): number | null {
  try {
    const v2 = localStorage.getItem(`${STORAGE_V2_PREFIX}${accountId}`)
    if (v2) {
      const n = Number(v2)
      if (Number.isFinite(n)) return n
    }
    const legacy = localStorage.getItem(`${LEGACY_PREFIX}${accountId}`)
    if (legacy) {
      const n = Number(legacy)
      if (Number.isFinite(n)) return n
    }
  } catch {
    /* ignore */
  }
  return null
}

/** Set during signup onboarding (artist + team paths); cleared when the user settles in (e.g. Music) or next OTP login. */
function isNewSignupFirstSession(): boolean {
  try {
    return localStorage.getItem('isNewSignup') === 'true'
  } catch {
    return false
  }
}

/** True when the signed-in user should see the daily digest (first time or ≥24h since last completion). */
export function isDigestEligible(accountId: string): boolean {
  if (!accountId) return false
  if (isNewSignupFirstSession()) return false
  const last = readLastSeenMs(accountId)
  if (last == null) return true
  return Date.now() - last >= DIGEST_INTERVAL_MS
}

export function markDigestSeen(accountId: string): void {
  try {
    localStorage.setItem(`${STORAGE_V2_PREFIX}${accountId}`, String(Date.now()))
  } catch {
    /* quota */
  }
}
